import { useAlertsStore } from "@/stores/alerts";

export type IntegrationWindowResult = {
  close: boolean;
  error: string;
  success: boolean;
  resData?: Record<string, any>;
};

export type IntegrationWindowOptions = {
  /** Called while the popup is open. Return true once the server has the connection. */
  poll?: () => Promise<boolean>;
  pollIntervalMs?: number;
};

export const integrationWindow = async (
  url: string,
  onSuccess?: string,
  about = "_black",
  options?: IntegrationWindowOptions
): Promise<IntegrationWindowResult> => {
  const userToast = useAlertsStore();
  const width = 1000;
  const height = 600;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;

  const w = window.open(url, about, `left=${left}, top=${top}, width=${width}, height=${height}`);
  if (!w) {
    userToast.addAlert("error", "Pop-up blocked. Allow pop-ups and try again.", 7);
    return { close: true, error: "popup blocked", success: false };
  }

  return await new Promise<IntegrationWindowResult>((resolve) => {
    let settled = false;
    let polling = false;
    let watchClosed = 0;
    let watchPoll = 0;

    const finish = (result: IntegrationWindowResult) => {
      if (settled) return;
      settled = true;
      window.clearInterval(watchClosed);
      window.clearInterval(watchPoll);
      window.removeEventListener("message", onMessage);
      if (result.error) {
        userToast.addAlert("error", result.error, 7);
      }
      if (result.success && onSuccess) {
        userToast.addAlert("success", onSuccess, 7);
      }
      resolve(result);
    };

    const onMessage = (e: MessageEvent) => {
      if (e?.data?.sender !== "ptc_callback") {
        return;
      }
      if (e?.data?.close) {
        w.close();
      }
      finish({
        close: !!e?.data?.close,
        error: (e?.data?.error as string) || "",
        success: !!e?.data?.success,
        resData: e?.data?.resData,
      });
    };

    window.addEventListener("message", onMessage);

    const confirmClosed = () => {
      if (!options?.poll) {
        finish({ close: true, error: "", success: false });
        return;
      }
      options
        .poll()
        .then((connected) => {
          finish({ close: true, error: "", success: connected });
        })
        .catch(() => {
          finish({ close: true, error: "", success: false });
        });
    };

    watchClosed = window.setInterval(() => {
      if (settled || !w.closed) return;
      window.clearInterval(watchClosed);
      confirmClosed();
    }, 200);

    if (options?.poll) {
      const poll = options.poll;
      watchPoll = window.setInterval(() => {
        if (settled || polling) return;
        polling = true;
        poll()
          .then((connected) => {
            if (!connected || settled) return;
            w.close();
            finish({ close: true, error: "", success: true });
          })
          .catch(() => {
            // The popup can still post a message, or a later poll can succeed.
          })
          .finally(() => {
            polling = false;
          });
      }, options.pollIntervalMs ?? 1000);
    }
  });
};
